import { useEffect } from "react";
import { usePrevious } from "../../../core";
import { NumberField, NumberFieldProps } from "../../inputs";
import { TableColumnDef } from "../types";
import { defaultPrettifyFilterValue } from "../utils";

import styles from "./styles.module.scss";

export function NumberColumn<T extends object = {}>(
  { numberFieldProps, secondNumberFieldProps, ...params }: TableColumnDef<T> & {
    numberFieldProps?: NumberFieldProps,
    secondNumberFieldProps?: NumberFieldProps
  },
): TableColumnDef<T> {
  return {
    enableColumnFilter: true,
    minSize: 50,
    cell: ({ renderValue }) => <span className={styles.content}>{renderValue() as any}</span>,
    ...params,
    meta: {
      defaultFilterFn: "equals",
      additionalFilterFn: [ "equals", "inNumberRange" ],
      prettifyFilterValue: ((v: any, filterFn) => {
        if (filterFn === "inNumberRange") {
          if (Array.isArray(v)) {
            const low = v[0];
            const high = v[1];

            if (
              low !== null && low !== undefined &&
              high !== null && high !== undefined
            ) {
              return ({ value: `${low} - ${high}` });
            } else if (low !== null && low !== undefined) {
              return ({ value: `More than ${low}` });
            } else if (high !== null && high !== undefined) {
              return ({ value: `Up to ${high}` });
            }
          }
        }

        return defaultPrettifyFilterValue(v, filterFn);
      }),
      Filter: ({ column, label, value, onChange, autoFocus, filterFn }) => {
        const previousFilterFn = usePrevious(filterFn);

        useEffect(() => {
          if (!!previousFilterFn && previousFilterFn !== filterFn) {
            if (filterFn === "inNumberRange") {
              onChange([ value, null ]);
            } else {
              onChange(Array.isArray(value) ? value?.[0] : null);
            }
          }
        }, [ filterFn, onChange, previousFilterFn, value ]);


        if (filterFn === "inNumberRange") {
          return (
            <>
              <NumberField
                autoFocus={autoFocus}
                placeholder="From"
                value={Array.isArray(value) ? value?.[0] : null}
                onChange={v => onChange([ v, Array.isArray(value) ? value?.[1] : null ])}
                {...numberFieldProps}
              />
              <NumberField
                placeholder="To"
                value={Array.isArray(value) ? value?.[1] : null}
                onChange={v => onChange([ Array.isArray(value) ? value?.[0] : null, v ])}
                {...secondNumberFieldProps}
              />
            </>
          );
        }
        return (
          <NumberField
            autoFocus={autoFocus}
            label={label}
            value={value as any}
            onChange={onChange}
            {...numberFieldProps}
          />
        );
      },
      ...params?.meta,
      excelProps: {
        totalsRowFunction: "sum",
        filterButton: true,
        ...params?.meta?.excelProps,
      },
    },
  };
}
